import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import LocationStep from "@/components/LocationStep";
import { 
  Calculator, Building2, MapPin, Calendar, Star, Plus, Trash2, 
  FileText, Download, CreditCard, ChevronRight, ChevronLeft, 
  Check, AlertCircle, DollarSign, TrendingUp, Home, Camera,
  Sparkles, RefreshCw, Clock, FileCheck, Users, Lock, Unlock, EyeOff
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

import { MEXICAN_CITIES as CITIES } from "@/constants/mexicanCities";

// Property types
const PROPERTY_TYPES = [
  { value: "departamento", label: "Departamento" },
  { value: "casa", label: "Casa" },
  { value: "casa_condominio", label: "Casa en Condominio" },
  { value: "penthouse", label: "Penthouse" },
  { value: "loft", label: "Loft" },
  { value: "terreno", label: "Terreno" },
  { value: "oficina", label: "Oficina" },
  { value: "local", label: "Local Comercial" }
];

// Conservation scale
const CONSERVATION_LEVELS = [
  { value: 1, label: "Muy malo", description: "Requiere reparaciones mayores" },
  { value: 2, label: "Malo", description: "Deterioro visible significativo" },
  { value: 3, label: "Regular", description: "Uso normal, mantenimiento básico" },
  { value: 4, label: "Bueno", description: "Bien conservado, detalles menores" },
  { value: 5, label: "Excelente", description: "Como nuevo o remodelado" }
];

// Credit packages
const CREDIT_PACKAGES = {
  single: { reports: 1, price: 99, label: "1 Reporte" },
  pack_5: { reports: 5, price: 299, label: "5 Reportes", savings: "40%" },
  pack_10: { reports: 10, price: 499, label: "10 Reportes", savings: "50%" }
};

const ValuationModule = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("new");
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [history, setHistory] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [agents, setAgents] = useState([]);
  const [valuationResult, setValuationResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form state - Location (new step 2)
  const [location, setLocation] = useState({
    lat: "",
    lng: "",
    colonia: "",
    municipio: "",
    estado: "",
    full_address: "",
    nse: "",
    infraestructura_notes: ""
  });
  
  // Form state - Property Info
  const [propertyInfo, setPropertyInfo] = useState({
    description: "",
    municipality: "",
    neighborhood: "",
    property_type: "departamento",
    m2: "",
    age: "",
    conservation: 3,
    catastral_value: "",
    land_use: "Habitacional",
    nse: "Medio-Alto",
    infrastructure_notes: ""
  });
  
  // Form state - Comparables
  const [comparables, setComparables] = useState([
    { price: "", m2: "", age: "", conservation: 3, source: "" },
    { price: "", m2: "", age: "", conservation: 3, source: "" },
    { price: "", m2: "", age: "", conservation: 3, source: "" }
  ]);
  
  // Form state - Market params
  const [marketParams, setMarketParams] = useState({
    cagr: 5.0,
    absorption_factor: 0.8
  });
  
  // Form state - Photos
  const [photos, setPhotos] = useState([]);

  const fetchCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/valuation/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(response.data);
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/valuation/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.valuations || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [token]);

  const fetchAgents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/valuation/agents-for-commission`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, [token]);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const response = await axios.post(`${API}/valuation/preview`, {
        property_info: {
          ...propertyInfo,
          m2: parseFloat(propertyInfo.m2) || 0,
          age: parseInt(propertyInfo.age) || 0,
          catastral_value: parseFloat(propertyInfo.catastral_value) || 0
        },
        comparables: comparables.map(c => ({
          price: parseFloat(c.price) || 0,
          m2: parseFloat(c.m2) || 0,
          age: parseInt(c.age) || 0,
          conservation: c.conservation,
          source: c.source
        })),
        cagr: marketParams.cagr,
        absorption_factor: marketParams.absorption_factor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || "No se pudo calcular el preview";
      toast.error(message);
    } finally {
      setPreviewLoading(false);
    }
  }, [token, propertyInfo, comparables, marketParams]);

  useEffect(() => {
    fetchCredits();
    fetchHistory();
    if (user?.role === "owner") {
      fetchAgents();
    }
  }, [fetchCredits, fetchHistory, fetchAgents, user?.role]);

  // Auto-fetch the paywall preview whenever the user reaches Step 5 (was 4)
  useEffect(() => {
    if (currentStep === 5 && validateStep(1) && validateStep(3)) {
      fetchPreview();
    } else {
      setPreviewData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => [...prev, reader.result.split(',')[1]]);
      };
      reader.readAsDataURL(file);
    });
  };

  const addComparable = () => {
    if (comparables.length < 6) {
      setComparables([...comparables, { price: "", m2: "", age: "", conservation: 3, source: "" }]);
    }
  };

  const removeComparable = (index) => {
    if (comparables.length > 3) {
      setComparables(comparables.filter((_, i) => i !== index));
    }
  };

  const updateComparable = (index, field, value) => {
    const updated = [...comparables];
    updated[index][field] = value;
    setComparables(updated);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return propertyInfo.description && propertyInfo.municipality &&
               propertyInfo.m2 && propertyInfo.age;
      case 2:
        // Location step: require at least lat/lng OR colonia
        return !!(location.lat && location.lng) || !!location.colonia;
      case 3:
        return comparables.every(c => c.price && c.m2);
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!credits || credits.total_available <= 0) {
      setShowPurchaseModal(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/valuation/create`, {
        property_info: {
          ...propertyInfo,
          m2: parseFloat(propertyInfo.m2),
          age: parseInt(propertyInfo.age),
          catastral_value: parseFloat(propertyInfo.catastral_value) || 0,
          // Location enrichment
          lat: location.lat ? parseFloat(location.lat) : null,
          lng: location.lng ? parseFloat(location.lng) : null,
          colonia: location.colonia || propertyInfo.neighborhood || "",
          full_address: location.full_address || "",
          nse: location.nse || propertyInfo.nse || "",
          infrastructure_notes: location.infraestructura_notes || propertyInfo.infrastructure_notes || ""
        },
        comparables: comparables.map(c => ({
          price: parseFloat(c.price),
          m2: parseFloat(c.m2),
          age: parseInt(c.age) || 0,
          conservation: c.conservation,
          source: c.source
        })),
        cagr: marketParams.cagr,
        absorption_factor: marketParams.absorption_factor,
        photos_base64: photos.length > 0 ? photos : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setValuationResult(response.data);
      setCurrentStep(6); // Results step (was 5)
      toast.success("Valuación generada exitosamente");
      fetchCredits();
      fetchHistory();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al generar valuación";
      toast.error(message);
      if (error.response?.status === 402) {
        setShowPurchaseModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (packageType) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/valuation/purchase-credits?package_type=${packageType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Paquete agregado exitosamente");
      fetchCredits();
      setShowPurchaseModal(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al procesar la compra";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignCommission = async (agentId) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/valuation/sign-commission-agreement?agent_id=${agentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Acuerdo firmado. Ya tienes 1 reporte mensual gratuito.");
      fetchCredits();
      setShowAgentModal(false);
    } catch (error) {
      toast.error("Error al firmar acuerdo");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPdf = async (valuationId) => {
    try {
      const response = await axios.get(`${API}/valuation/download/${valuationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `valuacion_${valuationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  // Render steps
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Descripción del Inmueble *</Label>
          <Textarea
            placeholder="Ej: Departamento de lujo en torre residencial con amenidades..."
            value={propertyInfo.description}
            onChange={(e) => setPropertyInfo({...propertyInfo, description: e.target.value})}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Ciudad/Municipio *</Label>
          <Select 
            value={propertyInfo.municipality} 
            onValueChange={(v) => setPropertyInfo({...propertyInfo, municipality: v})}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Selecciona ciudad" />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map(city => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Colonia/Zona</Label>
          <Input
            placeholder="Ej: Polanco, Roma Norte..."
            value={propertyInfo.neighborhood}
            onChange={(e) => setPropertyInfo({...propertyInfo, neighborhood: e.target.value})}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Tipo de Propiedad *</Label>
          <Select 
            value={propertyInfo.property_type} 
            onValueChange={(v) => setPropertyInfo({...propertyInfo, property_type: v})}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Superficie Construida (m²) *</Label>
          <Input
            type="number"
            placeholder="120"
            value={propertyInfo.m2}
            onChange={(e) => setPropertyInfo({...propertyInfo, m2: e.target.value})}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Antigüedad (años) *</Label>
          <Input
            type="number"
            placeholder="10"
            value={propertyInfo.age}
            onChange={(e) => setPropertyInfo({...propertyInfo, age: e.target.value})}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Valor Catastral (MXN, opcional)</Label>
          <Input
            type="number"
            placeholder="1500000"
            value={propertyInfo.catastral_value}
            onChange={(e) => setPropertyInfo({...propertyInfo, catastral_value: e.target.value})}
            className="mt-1"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label>Estado de Conservación: {CONSERVATION_LEVELS[propertyInfo.conservation - 1]?.label}</Label>
          <p className="text-sm text-slate-500 mb-2">{CONSERVATION_LEVELS[propertyInfo.conservation - 1]?.description}</p>
          <div className="flex gap-2 mt-1">
            {CONSERVATION_LEVELS.map(level => (
              <Button
                key={level.value}
                variant={propertyInfo.conservation === level.value ? "default" : "outline"}
                size="sm"
                onClick={() => setPropertyInfo({...propertyInfo, conservation: level.value})}
                className={propertyInfo.conservation === level.value ? "bg-primary" : ""}
              >
                {level.value} <Star className={`w-3 h-3 ml-1 ${propertyInfo.conservation >= level.value ? "fill-current" : ""}`} />
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold">Propiedades Comparables (Testigos)</h3>
          <p className="text-sm text-slate-500">Mínimo 3 comparables, máximo 6</p>
        </div>
        {comparables.length < 6 && (
          <Button variant="outline" size="sm" onClick={addComparable}>
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        )}
      </div>
      
      <div className="space-y-4">
        {comparables.map((comp, index) => (
          <Card key={index} className="border-l-4 border-l-primary">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <Badge variant="outline">Testigo {index + 1}</Badge>
                {comparables.length > 3 && (
                  <Button variant="ghost" size="sm" onClick={() => removeComparable(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs">Precio Lista (MXN) *</Label>
                  <Input
                    type="number"
                    placeholder="5000000"
                    value={comp.price}
                    onChange={(e) => updateComparable(index, "price", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Superficie (m²) *</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={comp.m2}
                    onChange={(e) => updateComparable(index, "m2", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Antigüedad (años)</Label>
                  <Input
                    type="number"
                    placeholder="5"
                    value={comp.age}
                    onChange={(e) => updateComparable(index, "age", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Conservación</Label>
                  <Select 
                    value={String(comp.conservation)} 
                    onValueChange={(v) => updateComparable(index, "conservation", parseInt(v))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSERVATION_LEVELS.map(level => (
                        <SelectItem key={level.value} value={String(level.value)}>{level.value} - {level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fuente</Label>
                  <Input
                    placeholder="Inmuebles24"
                    value={comp.source}
                    onChange={(e) => updateComparable(index, "source", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-4">Parámetros de Mercado</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>Tasa de Crecimiento Anual (CAGR): {marketParams.cagr}%</Label>
            <Slider
              value={[marketParams.cagr]}
              onValueChange={([v]) => setMarketParams({...marketParams, cagr: v})}
              min={0}
              max={15}
              step={0.5}
              className="mt-3"
            />
            <p className="text-xs text-slate-500 mt-1">Proyección de plusvalía anual del mercado local</p>
          </div>
          
          <div>
            <Label>Factor de Absorción: {(marketParams.absorption_factor * 100).toFixed(0)}%</Label>
            <Slider
              value={[marketParams.absorption_factor * 100]}
              onValueChange={([v]) => setMarketParams({...marketParams, absorption_factor: v / 100})}
              min={50}
              max={100}
              step={5}
              className="mt-3"
            />
            <p className="text-xs text-slate-500 mt-1">Velocidad de venta del mercado (80% = mercado activo)</p>
          </div>
        </div>
      </div>
      
      <div>
        <h3 className="font-semibold mb-4">Fotografías (Opcional)</h3>
        <p className="text-sm text-slate-500 mb-3">
          Sube hasta 4 fotos para análisis con IA de características que impactan el valor
        </p>
        
        <div className="flex flex-wrap gap-3">
          {photos.map((_, index) => (
            <div key={index} className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center border">
              <Camera className="w-8 h-8 text-slate-400" />
            </div>
          ))}
          
          {photos.length < 4 && (
            <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <Plus className="w-6 h-6 text-slate-400" />
            </label>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const formatMxn = (n) => `$${Math.round(n || 0).toLocaleString('es-MX')}`;
    const hasCredits = (credits?.total_available || 0) > 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Inmueble Sujeto
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p><strong>Tipo:</strong> {PROPERTY_TYPES.find(t => t.value === propertyInfo.property_type)?.label}</p>
              <p><strong>Ubicación:</strong> {propertyInfo.neighborhood}, {propertyInfo.municipality}</p>
              <p><strong>Superficie:</strong> {propertyInfo.m2} m²</p>
              <p><strong>Antigüedad:</strong> {propertyInfo.age} años</p>
              <p><strong>Conservación:</strong> {propertyInfo.conservation}/5</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calculator className="w-4 h-4" /> Comparables
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p><strong>Testigos:</strong> {comparables.length} propiedades</p>
              <p><strong>Rango de precios:</strong> {formatMxn(Math.min(...comparables.map(c => parseFloat(c.price) || 0)))} - {formatMxn(Math.max(...comparables.map(c => parseFloat(c.price) || 0)))}</p>
              <p><strong>CAGR:</strong> {marketParams.cagr}%</p>
              <p><strong>Factor absorción:</strong> {(marketParams.absorption_factor * 100).toFixed(0)}%</p>
              <p><strong>Fotos para análisis:</strong> {photos.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* PAYWALL PREVIEW TEASER */}
        <Card className="border-2 border-primary/40 bg-gradient-to-br from-primary/5 via-white to-primary/10 overflow-hidden relative" data-testid="paywall-preview-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Preview gratuito de tu Valuación</CardTitle>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Vista parcial
              </Badge>
            </div>
            <CardDescription>
              Cálculo determinístico (Matriz de Homologación NMX-R-081-SCFI). Te mostramos un rango aproximado — el valor exacto se desbloquea con 1 crédito.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Calculando preview...
              </div>
            ) : previewData ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur rounded-lg p-4 border" data-testid="preview-commercial-range">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Valor Comercial estimado (rango ±10%)
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {formatMxn(previewData.commercial_range.low)} – {formatMxn(previewData.commercial_range.high)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">MXN — basado en {previewData.comparables_used} testigos homologados</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-lg p-4 border">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <Home className="w-3 h-3" /> Precio por m² estimado
                    </p>
                    <p className="text-2xl font-bold text-slate-700">
                      {formatMxn(previewData.per_m2_range.low)} – {formatMxn(previewData.per_m2_range.high)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">MXN/m²</p>
                  </div>
                </div>

                {/* Locked content list */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 border border-dashed">
                  <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Se desbloquea con 1 crédito:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Valor Comercial exacto</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Valor Hipotecario e iBuying</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Plusvalía a 12, 24 y 36 meses</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Dictamen técnico con IA</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Análisis visual de fotos</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> PDF profesional descargable</div>
                  </div>
                </div>

                {/* Credit / Purchase status */}
                <div className={`rounded-lg p-4 flex items-start gap-3 ${hasCredits ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                  {hasCredits ? (
                    <>
                      <Unlock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-green-800">
                          Tienes {credits?.total_available} crédito{credits?.total_available !== 1 ? "s" : ""} disponible{credits?.total_available !== 1 ? "s" : ""}
                        </p>
                        <p className="text-sm text-green-700">
                          {(credits?.free_reports_remaining || 0) > 0
                            ? `${credits.free_reports_remaining} gratuito${credits.free_reports_remaining !== 1 ? "s" : ""} este mes + ${credits?.purchased_credits || 0} comprado${credits?.purchased_credits !== 1 ? "s" : ""}.`
                            : `${credits?.purchased_credits || 0} crédito${credits?.purchased_credits !== 1 ? "s" : ""} comprado${credits?.purchased_credits !== 1 ? "s" : ""} listo${credits?.purchased_credits !== 1 ? "s" : ""} para usar.`}
                          {" "}Al desbloquear se descontará 1 crédito.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">No tienes créditos disponibles</p>
                        <p className="text-sm text-amber-700">
                          Compra un paquete desde ${CREDIT_PACKAGES.single.price} MXN para desbloquear el reporte completo. El preview de arriba seguirá visible.
                        </p>
                        <Button
                          size="sm"
                          className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => setShowPurchaseModal(true)}
                          data-testid="paywall-buy-button"
                        >
                          <CreditCard className="w-4 h-4 mr-1" /> Ver paquetes
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-slate-500 text-sm">
                Completa los pasos anteriores para ver el preview.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderResults = () => (
    <div className="space-y-6">
      <div className="text-center py-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl">
        <Sparkles className="w-12 h-12 text-primary mx-auto mb-3" />
        <h2 className="text-2xl font-bold text-slate-800">Valuación Completada</h2>
        <p className="text-slate-500">Tu reporte profesional está listo</p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-2 border-primary">
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Valor Comercial</p>
            <p className="text-xl font-bold text-primary">
              ${valuationResult?.values?.commercial?.toLocaleString()}
            </p>
            <p className="text-xs text-slate-400">MXN</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Valor Hipotecario</p>
            <p className="text-lg font-semibold">
              ${valuationResult?.values?.mortgage?.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Valor iBuying</p>
            <p className="text-lg font-semibold">
              ${valuationResult?.values?.ibuying?.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Precio por m²</p>
            <p className="text-lg font-semibold">
              ${valuationResult?.values?.per_m2?.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" /> Proyección de Plusvalía
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-slate-500">12 meses</p>
              <p className="font-bold text-green-600">${valuationResult?.appreciation?.pv_12m?.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-slate-500">24 meses</p>
              <p className="font-bold text-green-600">${valuationResult?.appreciation?.pv_24m?.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-slate-500">36 meses</p>
              <p className="font-bold text-green-600">${valuationResult?.appreciation?.pv_36m?.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3 justify-center">
        <Button 
          onClick={() => downloadPdf(valuationResult.valuation_id)}
          className="bg-primary hover:bg-primary/90"
        >
          <Download className="w-4 h-4 mr-2" /> Descargar PDF
        </Button>
        <Button 
          variant="outline"
          onClick={() => {
            setCurrentStep(1);
            setValuationResult(null);
            setPropertyInfo({
              description: "", municipality: "", neighborhood: "",
              property_type: "departamento", m2: "", age: "", conservation: 3,
              catastral_value: "", land_use: "Habitacional", nse: "Medio-Alto",
              infrastructure_notes: ""
            });
            setLocation({
              lat: "", lng: "", colonia: "", municipio: "", estado: "",
              full_address: "", nse: "", infraestructura_notes: ""
            });
            setComparables([
              { price: "", m2: "", age: "", conservation: 3, source: "" },
              { price: "", m2: "", age: "", conservation: 3, source: "" },
              { price: "", m2: "", age: "", conservation: 3, source: "" }
            ]);
            setPhotos([]);
          }}
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Nueva Valuación
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Valuación Inmobiliaria (MVIP)
          </h1>
          <p className="text-slate-500">Motor de Valuación Inmobiliaria Progresiva</p>
        </div>
        
        <div className="flex items-center gap-3">
          {user?.role === "owner" && !credits?.has_commission_agreement && (
            <Button variant="outline" onClick={() => setShowAgentModal(true)}>
              <Users className="w-4 h-4 mr-2" /> Firmar Acuerdo
            </Button>
          )}
          
          <Card className="px-4 py-2">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-primary" />
              <div>
                <p className="text-xs text-slate-500">Reportes Disponibles</p>
                <p className="font-bold text-lg">{credits?.total_available || 0}</p>
              </div>
            </div>
          </Card>
          
          <Button onClick={() => setShowPurchaseModal(true)}>
            <CreditCard className="w-4 h-4 mr-2" /> Comprar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="new">Nueva Valuación</TabsTrigger>
          <TabsTrigger value="history">Historial ({history.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="new" className="mt-4">
          {currentStep < 6 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {currentStep === 1 && "Paso 1: Datos del Inmueble"}
                    {currentStep === 2 && "Paso 2: Geolocalización"}
                    {currentStep === 3 && "Paso 3: Propiedades Comparables"}
                    {currentStep === 4 && "Paso 4: Parámetros y Fotos"}
                    {currentStep === 5 && "Paso 5: Confirmar y Generar"}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(step => (
                      <div
                        key={step}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          currentStep === step 
                            ? "bg-primary text-white" 
                            : currentStep > step 
                              ? "bg-green-500 text-white" 
                              : "bg-slate-200 text-slate-500"
                        }`}
                      >
                        {currentStep > step ? <Check className="w-4 h-4" /> : step}
                      </div>
                    ))}
                  </div>
                </div>
                <Progress value={(currentStep / 5) * 100} className="h-1 mt-2" />
              </CardHeader>
              
              <CardContent>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && <LocationStep location={location} onChange={setLocation} />}
                {currentStep === 3 && renderStep2()}
                {currentStep === 4 && renderStep3()}
                {currentStep === 5 && renderStep4()}
              </CardContent>
              
              <CardFooter className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                
                {currentStep < 5 ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!validateStep(currentStep)}
                    data-testid="step-next-button"
                  >
                    {currentStep === 4 ? "Ver Preview" : "Siguiente"} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (credits?.total_available || 0) > 0 ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading || previewLoading}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="unlock-valuation-button"
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generando reporte completo...
                      </>
                    ) : (
                      <>
                        <Unlock className="w-4 h-4 mr-2" /> Desbloquear Reporte Completo (1 crédito)
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => setShowPurchaseModal(true)}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="paywall-purchase-button"
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Comprar crédito para desbloquear
                  </Button>
                )}
              </CardFooter>
            </Card>
          ) : (
            renderResults()
          )}
        </TabsContent>
        
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Valuaciones</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No tienes valuaciones anteriores</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((val) => (
                    <div 
                      key={val.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Home className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{val.property_info?.description?.slice(0, 50)}...</p>
                          <p className="text-sm text-slate-500">
                            {val.property_info?.municipality} | {val.property_info?.m2} m²
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-primary">
                            ${val.result?.values?.commercial?.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(val.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadPdf(val.id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purchase Credits Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Comprar Reportes de Valuación</DialogTitle>
            <DialogDescription>
              Selecciona un paquete de reportes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {Object.entries(CREDIT_PACKAGES).map(([key, pkg]) => (
              <div 
                key={key}
                className="flex items-center justify-between p-4 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                onClick={() => handlePurchase(key)}
              >
                <div>
                  <p className="font-medium">{pkg.label}</p>
                  {pkg.savings && (
                    <Badge variant="secondary" className="mt-1">Ahorra {pkg.savings}</Badge>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">${pkg.price} MXN</p>
                  <p className="text-xs text-slate-500">${(pkg.price / pkg.reports).toFixed(0)}/reporte</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Commission Agreement Modal */}
      <Dialog open={showAgentModal} onOpenChange={setShowAgentModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Firmar Acuerdo de Comisión</DialogTitle>
            <DialogDescription>
              Selecciona un agente inmobiliario para firmar un acuerdo de comisión y desbloquear 1 reporte de valuación mensual gratuito.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[300px] py-4">
            <div className="space-y-3">
              {agents.map((agent) => (
                <div 
                  key={agent.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:border-primary cursor-pointer"
                  onClick={() => handleSignCommission(agent.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      {agent.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{agent.name}</p>
                      <p className="text-sm text-slate-500">{agent.agency || "Agente independiente"}</p>
                      <p className="text-xs text-slate-400">{agent.city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={agent.is_master_partner ? "default" : "outline"}>
                      {agent.is_master_partner ? "Master Partner" : "Agente"}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">{agent.properties_count} propiedades</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ValuationModule;
